import validationRules from "../../../config/validationRules";
import {
    confrimSeatSelection,
    deselectSeat,
    priceSummary,
    sessionDestroy,
} from "../../../services/counter/dashboard.service";
import { COMPANY_CONFIG_KEYS } from "../../../utility/constants";
import { formatDateToYMDHMS } from "../../../utility/helpers";
import {
    getCookieData,
    getFormattedDate,
    removeCookieData,
    setCookieData,
} from "../../../utility/utils";

const BookingModel = () => ({
    travelMode: "",
    bookingId: null,
    pnr: null,
    tripId: null,
    fromStationId: null,
    toStationId: null,
    companyId: null,
    fareId: null,
    baseFare: 0,
    fromDistrictCode: "",
    toDistrictCode: "",
    busType: "",
    busTypeId: null,
    tripRouteId: null,
    tripScheduleId: null,
    isAc: false,
    sessionId: "",
    expireAt: null,
    expireTimeLimit: 0,
    maxSelection: 0,
    departureTime: "",
    tripDepartureTime: "",
    boardingCounters: [],
    allDroppingCounter: [],
    droppingCounters: [],
    levels: [],
    activeLevel: {},
    activeSection: {},
    decks: [],
    totalSeats: 0,
    availableSeats: 0,
    blockedSeats: 0,
    previousBookedSeats: 0,
    soldSeats: 0,
    seats: [],
    bookedSeats: [],
    cancellingSeats: [],
    isPassengerDetailsNecessary: false,
    hasGenderRestrictions: false,
    isArrived: false,
    isDeparted: false,
    coupon: [],
    ticket_number: null,
    isPurchase: true,
    expireDate: null,
    maxExpireDate: null,
    isApplyMyBalance: false,
    isAllowToPurchaseMultiSection: false,
    isAllowedToPrint: false,
    bookingMode: "", // status of previous seat => sold/booked
    previousBookedPassengerInfo: null,
    tripIsActive: true,
    dataFetchTime: null,
});

const COUNTER_PANEL_BOOKING_SESSION_COOKIE_NAME =
    "counterPanelBookingSessionId";

const setBookingSession = (sessionId, expireAt) => {
    setCookieData(
        COUNTER_PANEL_BOOKING_SESSION_COOKIE_NAME,
        sessionId,
        expireAt
    );
};

const getBookingSession = () => {
    return getCookieData(COUNTER_PANEL_BOOKING_SESSION_COOKIE_NAME);
};

const removeBookingSession = () => {
    removeCookieData(COUNTER_PANEL_BOOKING_SESSION_COOKIE_NAME);
};

const destroyBookingSession = async (isLocalSessionOnly = true) => {
    try {
        const session = getBookingSession();
        if (session) {
            removeBookingSession();
            if (!isLocalSessionOnly) {
                await sessionDestroy(session);
            }
        }
    } catch (e) {
        console.log("session detroy error");
    }
};

const SeatColors = {
    soldMale: "#FF0000",
    soldFemale: "#FF1493",
    bookedMale: "#FFA4A4",
    bookedFemale: "#FF99FF",
    available: "#FFFFFF",
    blocked: "#ffe7e7",
    quotaBlocked: "#AAAAAA",
    selected: "#008000",
};

// define seat background and title color
const getSeatColor = (seat) => {
    let backgroundColor = SeatColors.available;
    let textColor = "black";
    try {
        switch (seat.status) {
            case "sold":
                backgroundColor = seat.is_booked_by_female
                    ? SeatColors.soldFemale
                    : SeatColors.soldMale;
                textColor = "white";
                break;
            case "booked":
                backgroundColor = seat.is_booked_by_female
                    ? SeatColors.bookedFemale
                    : SeatColors.bookedMale;
                break;
            case "blocked":
                backgroundColor = SeatColors.blocked;
                break;
            case "quota_blocked":
                backgroundColor = SeatColors.quotaBlocked;
                break;
            case "selected":
                backgroundColor = SeatColors.selected;
                textColor = "white";
                break;
            default:
                break;
        }
    } catch (e) {}

    return { backgroundColor, textColor };
};

const prepareBusLayout = (allDeck = [], existingSeats = []) => {
    let totalBlocked = 0;
    const hasPreviousSeats = existingSeats.length;
    const updatedExistingSeats = [];
    let hasCoupon = false;
    const multiSaleUnavailableSeats = [];

    const formattedDecks = allDeck.map((deck, deckIndex) => {
        const numberOfColumn = deck.number_of_column;
        const numberOfRow = deck.number_of_row;

        const columns = {};

        for (let i = 1; i <= numberOfColumn; i++) {
            const rows = [];
            for (let x = 1; x <= numberOfRow; x++) {
                rows.push({
                    row: x,
                    column: i,
                    id: null,
                });
            }

            columns[i] = rows;
        }

        deck.seats.forEach((seat) => {
            if (seat.status === "blocked") totalBlocked++;

            if (!hasPreviousSeats && seat.satatus == "selected")
                seat.status === "available";

            if (hasPreviousSeats) {
                const existingSeat = existingSeats.find(
                    ({ id }) => id == seat.id
                );

                if (existingSeat) {
                    if (seat.status === "selected") {
                        const updatedSeat = { ...seat };
                        if (existingSeat.coupon) {
                            updatedSeat.coupon = existingSeat.coupon;
                            hasCoupon = true;
                        }
                        updatedExistingSeats.push(updatedSeat);
                    } else {
                        multiSaleUnavailableSeats.push(seat.title);
                    }
                }
            }

            const color = getSeatColor(seat);
            columns[seat.column_number][Number(seat.row_number) - 1] = {
                ...seat,
                deckIndex,
                columnIndex: Number(seat.column_number) - 1,
                ...color,
            };
        });

        deck.seats = Object.values(columns);
        return deck;
    });

    return {
        decks: formattedDecks,
        totalBlocked,
        updatedExistingSeats,
        hasCoupon,
        multiSaleUnavailableSeats,
    };
};

const prepareShipLayout = (allDeck = [], decks, existingSeats) => {
    const numberOfColumn = allDeck[0].number_of_column;
    let totalBlocked = 0;
    const updatedExistingSeats = [];
    let hasCoupon = false;
    const multiSaleUnavailableSeats = [];

    let sections = decks.map((item) => ({ ...item, seats: [] }));

    const hasPreviousSeats = existingSeats.length;
    allDeck[0].seats.forEach((seat) => {
        if (seat.status === "blocked") totalBlocked++;
        const section = sections.find(({ id }) => id === seat.section_id);

        if (section) {
            if (!hasPreviousSeats && seat.satatus === "selected")
                seat.status === "available";

            if (hasPreviousSeats) {
                const existingSeat = existingSeats.find(
                    ({ id }) => id == seat.id
                );

                if (existingSeat) {
                    if (seat.status === "selected") {
                        const updatedSeat = { ...seat };
                        if (existingSeat.coupon) {
                            updatedSeat.coupon = existingSeat.coupon;
                            hasCoupon = true;
                        }
                        updatedExistingSeats.push(updatedSeat);
                    } else {
                        multiSaleUnavailableSeats.push(seat.title);
                    }
                }
            }
            section.seats.push(seat);
        }
    });

    let lastMax = 0;

    sections = sections.map((section, sectionIndex) => {
        const formattedSeats = {};

        let minRow = section.seats[0].row_number;
        let maxRow = 0;
        section.seats.forEach((seat) => {
            if (seat.row > maxRow) maxRow = seat.row_number;

            if (seat.row < minRow) minRow = seat.row_number;
        });

        for (let i = 1; i <= numberOfColumn; i++) {
            const rows = [];
            for (let x = minRow; x <= maxRow; x++) {
                rows.push("");
            }
            formattedSeats[i] = rows;
        }

        if (sectionIndex > 0 && minRow - lastMax > 1) {
            for (let y = 1; y < minRow - lastMax; y++)
                sections[sectionIndex - 1].seats.map((column) => {
                    column.push("");
                });
        }

        lastMax = maxRow;

        section.seats.forEach((seat) => {
            const color = getSeatColor(seat);
            formattedSeats[seat.column][Number(seat.row) - minRow] = {
                ...seat,
                sectionIndex,
                columnIndex: Number(seat.column) - 1,
                ...color,
            };
        });

        section.seats = Object.values(formattedSeats);
        return section;
    });

    return {
        decks: sections,
        totalBlocked,
        updatedExistingSeats,
        hasCoupon,
        multiSaleUnavailableSeats,
    };
};

const applyFareChange = (decks, seats) => {
    const updatedSeats = [];

    try {
        seats.forEach((selectedSeat) => {
            decks.forEach((deck) => {
                deck.seats.forEach((column) => {
                    column.forEach((currentSeat) => {
                        if (
                            currentSeat &&
                            selectedSeat.id == currentSeat.id &&
                            selectedSeat.price != currentSeat.price
                        ) {
                            selectedSeat = currentSeat;
                        }
                    });
                });
            });

            updatedSeats.push(selectedSeat);
        });
    } catch (e) {}

    return updatedSeats;
};

const getDepartureArrivalTime = (counter, departureTimeFormat = "12") => {
    let time = "";
    try {
        const dateTime =
            counter.counter_type === "boarding"
                ? counter?.departure_time
                : counter?.arrival_time;
        if (dateTime) {
            time = getFormattedDate(
                dateTime,
                departureTimeFormat == "12"
                    ? "onlytime12withoutseconds"
                    : "onlytime24withoutseconds"
            );
        }
    } catch (e) {
        console.log(e);
    }

    return time;
};

const getBoardingAndDroppingCounters = (
    counters = [],
    counterId,
    ownCounterNameInBoardingPoint = false,
    departureTimeFormat = "12",
    formState,
    isRefresh = false,
    fromStationId = null
) => {
    let boardingCounters = [];
    let allDroppingCounter = [];
    let droppingCounters = [];
    for (const counter of counters) {
        if (counter) {
            const time = getDepartureArrivalTime(counter, departureTimeFormat);

            let label = counter.counter_title;

            if (time) label += ` [${time}]`;

            if (counter.counter_type === "boarding") {
                boardingCounters.push({
                    label,
                    value: counter.counter_id,
                    isFareSame: counter.is_fare_same,
                    stationId: counter.station_id,
                    to: counter.to,
                    date: counter.boarding_time,
                    departureTime: counter.departure_time,
                    time,
                    first: counter.first,
                });
            }

            if (counter.counter_type === "dropping") {
                allDroppingCounter.push({
                    label,
                    value: counter.counter_id,
                    isFareSame: counter.is_fare_same,
                    stationId: counter.station_id,
                    arrivalTime: counter.arrival_time,
                    time,
                    last: counter.last,
                });
            }
        }
    }

    if (boardingCounters.length) {
        if (
            boardingCounters.length > 1 &&
            counterId == fromStationId &&
            ownCounterNameInBoardingPoint
        ) {
            const ownCounter = boardingCounters.find(
                (counter) => counter.value === counterId
            );

            if (ownCounter) {
                boardingCounters = [ownCounter];
                droppingCounters = getDroppingCounters(
                    allDroppingCounter,
                    ownCounter
                );
            }
        }

        let selectedBoardingCounter = null;

        if (boardingCounters.length === 1) {
            selectedBoardingCounter = boardingCounters[0];
        } else if (isRefresh && formState?.boarding_counter?.value) {
            selectedBoardingCounter = formState.boarding_counter.value;
        }

        if (!selectedBoardingCounter) {
            selectedBoardingCounter = boardingCounters.find(
                ({ first }) => first
            );
            if (!selectedBoardingCounter && fromStationId) {
                selectedBoardingCounter = boardingCounters.find(
                    ({ stationId }) => stationId == fromStationId
                );
            }
        }

        if (selectedBoardingCounter) {
            droppingCounters = getDroppingCounters(
                allDroppingCounter,
                selectedBoardingCounter
            );
        }

        boardingCounters.sort(
            (a, b) => new Date(a.departureTime) - new Date(b.departureTime)
        );
    }

    return { boardingCounters, droppingCounters, allDroppingCounter };
};

const getDroppingCounters = (allDroppingCounter = [], boardingCounter = {}) => {
    try {
        return allDroppingCounter
            .filter(({ stationId }) => boardingCounter?.to?.includes(stationId))
            .sort((a, b) => new Date(a.arrivalTime) - new Date(b.arrivalTime));
    } catch (e) {}

    return [];
};

const WheelIcon = ({ color = "#A8A29E", ...rest }) => {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...rest}
        >
            <path
                d="M12.9498 3.05026C11.6276 1.72811 9.86977 1 8 1C6.13023 1 4.37239 1.72811 3.05023 3.05026C1.72811 4.37239 1 6.13023 1 8C1 9.86977 1.72811 11.6276 3.05023 12.9497C4.37239 14.2719 6.13023 15 8 15C9.86977 15 11.6276 14.2719 12.9498 12.9497C14.2719 11.6276 15 9.86977 15 8C15 6.13023 14.2719 4.37239 12.9498 3.05026ZM8 2.56579C10.7558 2.56579 13.0388 4.62775 13.3878 7.28983L10.0193 6.96176C9.64161 6.23018 8.87853 5.7285 8 5.7285C7.12147 5.7285 6.35839 6.23018 5.98069 6.96176L2.61219 7.28983C2.9612 4.62775 5.24424 2.56579 8 2.56579ZM2.61025 8.69385L5.93883 8.95318C6.1491 9.40571 6.504 9.77803 6.94398 10.0101L7.16213 13.3696C4.78746 13.0005 2.91677 11.0883 2.61025 8.69385ZM8 8.70571C7.61084 8.70571 7.29428 8.38913 7.29428 8C7.29428 7.61087 7.6109 7.29428 8 7.29428C8.3891 7.29428 8.70571 7.6109 8.70571 8C8.70571 8.3891 8.38916 8.70571 8 8.70571ZM8.83792 13.3696L9.05607 10.0101C9.49606 9.77806 9.85093 9.40571 10.0612 8.95318L13.3898 8.69385C13.0832 11.0883 11.2125 13.0005 8.83792 13.3696Z"
                fill={color}
            />
        </svg>
    );
};

const saveSelection = async (
    { status, tripId, fareId, sessionId, seatId },
    queryParam
) => {
    return new Promise(async (resolve, reject) => {
        try {
            let payload = {
                trip_id: tripId,
                seat_id: seatId,
            };

            if (sessionId) {
                payload.session_id = sessionId;
            }

            let response;
            if (status === "available") {
                response = await deselectSeat(payload, queryParam);
            } else {
                payload.fare_id = fareId;
                response = await confrimSeatSelection(payload, queryParam);
            }

            resolve(response.data);
        } catch (e) {
            if (e.data?.code === 409) {
                resolve({
                    status: "blocked",
                    message:
                        e.data.error?.message || "Seat is already selected.",
                });
            }

            reject(e);
        }
    });
};

const isFullRoute = (booking, passengerInformation) => {
    try {
        if (
            booking.fromStationId ==
                passengerInformation?.boarding_counter?.value?.stationId &&
            booking.toStationId ==
                passengerInformation?.dropping_counter?.value?.stationId
        ) {
            return true;
        }
    } catch (error) {}
    return false;
};

const getSeatDiscount = async (booking, queryParam, passengerInformation) => {
    try {
        const selectedSeats = booking.seats;
        const payload = {
            from: booking.fromDistrictCode,
            to: booking.toDistrictCode,
            is_ac: booking.isAc,
            seat_ids: selectedSeats.map((item) => item.id),
            fare_id: booking.fareId,
            travel_mode: booking.travelMode,
            bus_type: booking.busType,
            bus_type_id: booking.busTypeId,
            company_id: booking.companyId,
            trip_id: booking.tripId,
            trip_route_id: booking.tripRouteId,
            service_date_time: booking.tripDepartureTime,
            is_full_route: isFullRoute(booking, passengerInformation),
            trip_schedule_id: booking.tripScheduleId,
            email: passengerInformation?.email?.value || null,
            phone: passengerInformation?.mobile?.value || null,
            coupon_data: selectedSeats
                .filter(({ coupon }) => coupon)
                .map((seat) => ({
                    seat_id: seat.id,
                    coupon: seat.coupon,
                })),
        };

        const response = await priceSummary(payload, queryParam);
        const seatFares = response?.data?.data?.seat_fares;
        const coupons = response?.data?.data?.coupon_info?.coupon || [];

        const fareMap = new Map(
            seatFares?.map((fare) => [fare?.seat_id, fare?.discount])
        );

        const updatedSelectedSeats = selectedSeats.map((seat) => {
            const discount = fareMap.get(seat?.id) || 0;
            return {
                ...seat,
                discount: Number(discount),
                coupon_last_value: getCoupon(coupons, seat?.id),
            };
        });

        return updatedSelectedSeats;
    } catch (error) {
        throw error;
    }
};

const getCoupon = (coupons, seatId) => {
    try {
        const couponInfo = coupons.find(({ seat_id }) => seat_id == seatId);
        return couponInfo?.coupon || "";
    } catch (e) {}

    return "";
};

const GenderOptions = [
    {
        value: "male",
        label: "Male",
    },
    {
        value: "female",
        label: "Female",
    },
];

const NationalityOptions = [
    {
        label: "Bangladeshi",
        value: 1,
    },
    {
        label: "Indian",
        value: 2,
    },
];

const PassengerFormDefaultFields = () => ({
    name: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
    mobile: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
    email: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
    gender: {
        isRequired: true,
        type: "string",
        value: GenderOptions[0],
        status: true,
        errorMessage: "",
    },
    address: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
    age: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
    passport_number: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
    nationality: {
        isRequired: true,
        type: "string",
        value: NationalityOptions[0],
        status: true,
        errorMessage: "",
    },
    boarding_place: {
        isRequired: true,
        type: "string",
        value: "",
        status: true,
        errorMessage: "",
    },
});

const getCounter = (counters = [], selected = null) => {
    try {
        if (counters.length) {
            if (selected) return selected;
            else if (counters.length === 1) return counters[0];
        }
    } catch (e) {}

    return "";
};

const PassengerFormCommonFields = (
    boardingCounters = [],
    droppingCounters = [],
    selectedBoardingCounter = null,
    selectedDroppingCounter = null
) => ({
    boarding_counter: {
        isRequired: true,
        type: "string",
        value: getCounter(boardingCounters, selectedBoardingCounter),
        errorMessage: "",
    },
    dropping_counter: {
        isRequired: true,
        type: "string",
        value: getCounter(droppingCounters, selectedDroppingCounter),
        errorMessage: "",
    },
    goods: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    gross_pay: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    goods_discount: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    net_pay: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    total_paid: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    refund: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    advanced_payment: {
        type: "string",
        value: "",
        errorMessage: "",
    },
    due: {
        type: "string",
        value: "",
        errorMessage: "",
    },
});

const getPassengerFormInformation = (
    companyConfig,
    boardingCounters,
    droppingCounters,
    fromCounterId = null,
    allDroppingCounter = null,
    fromStationId = null,
    toStationId = null
) => {
    let initialFields = PassengerFormDefaultFields();
    let selectedBoardingCounter = null;
    let selectedDroppingCounter = null;

    try {
        const config = companyConfig.find(
            (item) => item.key === COMPANY_CONFIG_KEYS.personal_information
        );
        if (config?.value) {
            const fields = JSON.parse(config.value);
            if (fields?.length) {
                initialFields = {};
                fields
                    .filter(({ status }) => status)
                    .forEach((field) => {
                        const key = field.name;
                        delete field.name;
                        let value = "";

                        if (key === "nationality")
                            value = NationalityOptions[0];
                        if (key === "gender") value = GenderOptions[0];

                        initialFields[key] = {
                            ...field,
                            value,
                            errorMessage: "",
                        };
                    });
            }
        }

        if (boardingCounters?.length) {
            if (boardingCounters?.length === 1) {
                selectedBoardingCounter = boardingCounters[0];
            }
            if (!selectedBoardingCounter) {
                selectedBoardingCounter = boardingCounters.find(
                    ({ first }) => first
                );
                if (!selectedBoardingCounter && fromStationId) {
                    selectedBoardingCounter = boardingCounters.find(
                        ({ stationId }) => stationId == fromStationId
                    );
                }
            }

            if (
                selectedBoardingCounter &&
                Array.isArray(allDroppingCounter) &&
                allDroppingCounter.length > 1
            ) {
                droppingCounters = getDroppingCounters(
                    allDroppingCounter,
                    selectedBoardingCounter
                );

                if (droppingCounters?.length === 1) {
                    selectedDroppingCounter = droppingCounters[0];
                } else {
                    selectedDroppingCounter = droppingCounters.find(
                        ({ last }) => last
                    );
                }
                 if (!selectedDroppingCounter && toStationId) {
                    selectedDroppingCounter = droppingCounters.find(
                        ({ stationId }) => stationId === toStationId
                    );
                }
            }
        }
    } catch (e) {
        console.log(e);
    }

    return {
        ...initialFields,
        ...PassengerFormCommonFields(
            boardingCounters,
            droppingCounters,
            selectedBoardingCounter,
            selectedDroppingCounter
        ),
    };
};

const checkPassengerFormInputValidity = (
    value,
    isRequired = false,
    validationType
) => {
    if (isRequired && !value) {
        return validationRules.default.required.message;
    } else if (!isRequired && !value) {
        return "";
    } else {
        const rule = validationRules[validationType];

        if (rule.minimumLength && value.length < rule.minimumLength) {
            return validationRules.default.minimumLength.message.replace(
                "{value}",
                rule.minimumLength
            );
        } else if (rule.maximumLength && value.length > rule.maximumLength) {
            return validationRules.default.maximumLength.message.replace(
                "{value}",
                rule.maximumLength
            );
        } else if (
            rule.pattern &&
            validationRules.validationTypes.pattern(
                rule.pattern?.value || rule.pattern,
                value
            )
        ) {
            return (
                rule.pattern.message || validationRules.default.pattern.message
            );
        }
    }

    // console.log(key, value, validationType, errorMessage);

    return "";
};

const getMergedPassengerInformationWithForm = (user, state) => {
    state = structuredClone(state);

    try {
        state.name.value = user.name || state.name.value;
        state.name.errorMessage = user.name ? "" : state.name.errorMessage;

        state.mobile.value = user.msisdn;
        state.mobile.errorMessage = user.msisdn
            ? ""
            : state.mobile.errorMessage;

        if (state.email) {
            state.email.value = user?.email || state.email?.value;
            state.email.errorMessage = user.email
                ? ""
                : state.email.errorMessage;
        }

        if (state.gender) {
            state.gender.value =
                GenderOptions.find(({ value }) => value === user.gender) ||
                GenderOptions[0];
        }

        if (state.age) {
            state.age.value = user.age || state.age.value;
            state.age.errorMessage = user.age ? "" : state.age.errorMessage;
        }

        if (state.address) {
            state.address.value = user.options?.address || state.address.value;
            state.address.errorMessage = user.options?.address
                ? ""
                : state.address.errorMessage;
        }

        if (state.passport_number) {
            state.passport_number.value =
                user.options?.passport_number || state.passport_number.value;
            state.passport_number.errorMessage = user.options?.passport_number
                ? ""
                : state.passport_number.errorMessage;
        }

        if (state.nationality) {
            state.nationality.value =
                NationalityOptions.find(
                    ({ value }) => value === user.options?.nationality
                ) || NationalityOptions[0];
        }

        if (state.boarding_place) {
            state.boarding_place.value =
                user.boarding_place || state.boarding_place.value;
            state.boarding_place.errorMessage = user.boarding_place
                ? ""
                : state.boarding_place.errorMessage;
        }

        state.advanced_payment.value =
            user.advanced_payment || state.advanced_payment.value;
        state.advanced_payment.errorMessage = user.advanced_payment
            ? ""
            : state.advanced_payment.errorMessage;

        state.goods_discount.value =
            user.goods_discount || state.goods_discount.value;
        state.goods_discount.errorMessage = user.goods_discount
            ? ""
            : state.goods_discount.errorMessage;

        state.goods.value = user.goods || state.goods.value;
        state.goods.errorMessage = user.goods ? "" : state.goods.errorMessage;

        state.net_pay.value = user.net_pay || state.net_pay.value;
        state.net_pay.errorMessage = user.net_pay
            ? ""
            : state.net_pay.errorMessage;

        state.gross_pay.value = user.gross_pay || state.gross_pay.value;
        state.gross_pay.errorMessage = user.gross_pay
            ? ""
            : state.gross_pay.errorMessage;
        state.total_paid.value = user.total_paid || state.total_paid.value;
        state.refund.value = user.refund ?? state.refund.value;

        if (user.boarding_counter) {
            state.boarding_counter.value = {
                label: user.boarding_counter.title,
                value: user.boarding_counter.id,
                isFareSame: user.boarding_counter.is_fare_same || true,
            };
        }

        if (user.dropping_counter) {
            state.dropping_counter.value = {
                label: user.dropping_counter.title,
                value: user.dropping_counter.id,
                isFareSame: user.boarding_counter.is_fare_same || true,
            };
        }
    } catch (e) {
        console.log(e);
    }

    return state;
};

const getPreviousBookedPassengerInfo = (bookingData) => {
    try {
        let previousBookedPassengerInfo = {
            boarding_counter: bookingData.boarding_counter,
            dropping_counter: bookingData.dropping_counter,
            boarding_place: bookingData.boarding_place || "",
            advanced_payment: bookingData.advanced_paid_amount || "",
        };

        if (bookingData.goods_operation) {
            const netPay = Number(
                bookingData.goods_operation.final_net_pay || 0
            );
            previousBookedPassengerInfo.goods_discount =
                Number(bookingData.goods_operation.gross_pay || 0) - netPay;
            previousBookedPassengerInfo.goods =
                bookingData.goods_operation.goods_weight_kg;
            previousBookedPassengerInfo.net_pay = netPay;
            previousBookedPassengerInfo.gross_pay =
                bookingData.goods_operation.gross_pay;
            previousBookedPassengerInfo.total_paid = "";

            previousBookedPassengerInfo.refund = "";
            // bookingData.goods_operation.refund === null
            //     ? 0
            //     : bookingData.goods_operation.refund;
        }

        if (bookingData.primary_passenger) {
            previousBookedPassengerInfo = {
                ...bookingData.primary_passenger,
                ...previousBookedPassengerInfo,
            };
        }
        return previousBookedPassengerInfo;
    } catch (e) {}

    return null;
};

const getBookingMaxDateTime = (
    counterId,
    ticketBookingExpiryTime,
    trip,
    companyConfig
) => {
    try {
        // Default time to subtract is 30 minutes if company config and user config is not found
        let timeToSubtract = 30;

        const counter = trip?.counters.find(
            ({ counter }) => counter?.id == counterId
        );

        const counterDepartureTime = counter?.departure_time
            ? counter?.departure_time
            : trip?.departure_time;

        const tripStartTime = new Date(
            trip?.departure_date + "T" + trip?.departure_time
        );

        let counterDepartureDateTime = new Date(
            trip?.departure_date + "T" + counterDepartureTime
        );

        // Add one day to counter departure date time
        if (tripStartTime > counterDepartureDateTime) {
            counterDepartureDateTime.setDate(
                counterDepartureDateTime.getDate() + 1
            );
        }

        const defaultExpiryTimeInUserConfig = Number(
            ticketBookingExpiryTime || 0
        );

        if (defaultExpiryTimeInUserConfig) {
            timeToSubtract = defaultExpiryTimeInUserConfig;
        } else {
            const counterBookingExpiryDefaultTime = companyConfig.find(
                (item) => {
                    return (
                        item?.key === "counter_booking_expiry_default_time" &&
                        item?.status === "active"
                    );
                }
            );
            const defaultExpiryTimeInCompanyConfig = Number(
                counterBookingExpiryDefaultTime?.value ||
                    process?.env?.DEFAULT_COUNTER_BOOKING_EXPIRY_TIME ||
                    20
            );

            if (defaultExpiryTimeInCompanyConfig) {
                timeToSubtract = defaultExpiryTimeInCompanyConfig;
            }
        }

        const timeToReturn = new Date(
            counterDepartureDateTime - timeToSubtract * 60 * 1000
        );

        return timeToReturn;
    } catch (e) {}
};

const getBookingPayload = (booking, counterId, formState) => {
    const selectedSeats = structuredClone(booking.seats);
    const payload = {
        session_id: booking.sessionId,
        user_id: counterId,
        expiry_time: booking.isPurchase
            ? null
            : formatDateToYMDHMS(
                  booking.expireDate || booking.maxExpireDate,
                  false
              ),
        book_type: booking.isPurchase ? "issue" : "booking",
        bookings: [
            {
                passengers: selectedSeats.map((seat, index) => {
                    return {
                        user_id: null, // Todo: counter user id
                        name: formState.name?.value || null,
                        msisdn: formState.mobile?.value || null,
                        email: formState.email?.value || null,
                        age: Number(formState.age?.value) || null,
                        gender: formState.gender?.value?.value || "male",
                        address: formState.address?.value || null,
                        passport: formState.passport_number?.value || null,
                        nationality: formState.nationality?.value?.value || 1,
                        seat_id: seat.id,
                        ticket_number: seat.ticket_number
                            ? parseFloat(seat.ticket_number)
                            : null,
                        is_primary: index == 0,
                    };
                }),
                boarding_place: formState.boarding_place?.value,
                goods_weight: Number(formState.goods?.value),
                goods_price: Number(formState.gross_pay?.value),
                goods_discount: Number(formState.goods_discount?.value),
                advanced_paid_amount:
                    !booking.isPurchase && formState.advanced_payment?.value
                        ? Number(formState.advanced_payment?.value)
                        : null,
                net_pay: Number(formState.net_pay?.value),
                total_paid: Number(formState.total_paid?.value),
                refund: Number(formState.refund?.value),
                fare_id: booking.fareId,
                boarding_counter_id: formState.boarding_counter?.value?.value,
                dropping_counter_id: formState.dropping_counter?.value?.value,
                trip_type: "up_trip",
            },
        ],
        ticket_number: booking.ticket_number
            ? Number(booking.ticket_number)
            : undefined,
        coupon_data: selectedSeats
            .filter((seat) => seat.discount)
            .map((seat) => {
                return {
                    coupon: seat.coupon,
                    seat_id: seat.id,
                };
            }),
    };

    return payload;
};

const getErrorMessage = (error, code = 400, prefixMessage = "") => {
    let message = `${prefixMessage} Something went wrong!`;

    try {
        const errorCode = error.data?.code;
        if (errorCode >= code && errorCode < code + 100) {
            message =
                error.data.error?.message || error.data.error?.title || message;
        }
    } catch (e) {}

    return message;
};

const passengerFormDropdDownStyles = {
    valueContainer: (provided) => ({
        ...provided,
        padding: "0 8px", // Adjust padding to fit the smaller size
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        marginTop: "8px",
        marginBottom: "8px",
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: "2px 4px", // Adjust padding for the dropdown indicator
        maxHeight: "32px",
    }),
    input: (provided) => ({
        ...provided,
        margin: "0 2px",
        padding: "0",
    }),
};

function findMaxRow(data) {
    let maxRow = 0;
    if (data && data.length > 0) {
        data.forEach((item) => {
            if (item.row_number > maxRow) {
                maxRow = item.row_number;
            }
        });
    }

    return maxRow;
}

function findMaxColumn(data) {
    let maxColumn = 0;
    if (data && data.length > 0) {
        data.forEach((item) => {
            if (item.column_number > maxColumn) {
                maxColumn = item.column_number;
            }
        });
    }

    return maxColumn;
}

const getCompanyCallCenterQueryParam = ({
    companyId,
    counterId,
    counterUserId,
}) => {
    let params = "";

    try {
        if (companyId) {
            params += `company_id=${companyId}`;
        }

        if (counterId) {
            if (params) params += "&";
            params += `counter_id=${counterId}&user_id=${counterUserId}`;
        }
    } catch (e) {}

    return params;
};

const PRINTERS = [
    // {
    //     id: "dot-matrix",
    //     value: "Dot-Matrix",
    //     label: "Dot-Matrix (8.50X3.66)",
    // },
    { id: "laser", value: "Laser", label: "Laser (A5:5.83X8.27)" },
    { id: "thermal", value: "Thermal", label: "Thermal (80mm)" },
    { id: "pos", value: "POS", label: "POS (80mm)" },
];

const getNetPay = (seats) => {
    return seats.reduce((accumulator, seat) => {
        const priceToAdd = seat.discount
            ? seat.price - seat.discount
            : seat.price;

        return accumulator + priceToAdd;
    }, 0);
};

export {
    applyFareChange,
    BookingModel,
    checkPassengerFormInputValidity,
    destroyBookingSession,
    GenderOptions,
    NationalityOptions,
    getBookingSession,
    getBookingMaxDateTime,
    getBookingPayload,
    getBoardingAndDroppingCounters,
    getCompanyCallCenterQueryParam,
    getDroppingCounters,
    getErrorMessage,
    getMergedPassengerInformationWithForm,
    getNetPay,
    getSeatColor,
    getSeatDiscount,
    getPassengerFormInformation,
    getPreviousBookedPassengerInfo,
    passengerFormDropdDownStyles,
    PassengerFormCommonFields,
    prepareBusLayout,
    prepareShipLayout,
    PRINTERS,
    removeBookingSession,
    saveSelection,
    setBookingSession,
    SeatColors,
    WheelIcon,
    findMaxRow,
    findMaxColumn,
};
